import React, { useMemo } from 'react';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { FeedPostModal } from '@components/modals/FeedPostModal';
import { UnlockFeedModal } from '@components/modals/FeedPostModal/UnlockFeedModal';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalType, useModal } from '@context/modal';
import { useBackendUserPersistedData } from '@hooks/useBackendUserPersistedData';
import { trackClickEvent } from '@lib/analytics';
import { PostInputUI } from './PostInputUI';

export function FeedPostInput() {
  const { authUser, isAuthenticatedWithAPI } = useAuth();
  const { openModal } = useModal();
  const { login } = useConnectedAuth();

  const { hasFeedAccess } = useBackendUserPersistedData();

  const hasPostingAccess = hasFeedAccess.type === 'has-access';

  const inputPlaceholder = useMemo(() => {
    if (isAuthenticatedWithAPI) {
      return `What sound is on your mind${
        authUser?.displayName ? `, ${authUser.displayName}...` : '?'
      }`;
    } else {
      return 'Sign in to post...';
    }
  }, [isAuthenticatedWithAPI, authUser?.displayName]);

  const isLocked = useMemo(() => {
    return Boolean(isAuthenticatedWithAPI) && !hasPostingAccess;
  }, [isAuthenticatedWithAPI, hasPostingAccess]);

  const onClick = useStableCallback(() => {
    if (!isAuthenticatedWithAPI) {
      login();
      return;
    }

    if (hasPostingAccess) {
      trackClickEvent({
        event: 'Create Post Clicked',
        properties: {
          sourceComponent: 'Feed Post Input',
        },
      });
      openModal(ModalType.FEED_POST, {
        body: (
          <FeedPostModal
            key="post-input"
            sourceLocation="Feed Post Input"
            preselectedResource={null}
          />
        ),
      });
    } else {
      openModal(ModalType.UNLOCK_FEED_POST, {
        body: (
          <UnlockFeedModal
            key="unlock-feed-post"
            sourceLocation="Feed Post Input"
            preselectedResource={null}
          />
        ),
      });
    }
  });

  return (
    <PostInputUI
      onClick={onClick}
      placeholder={inputPlaceholder}
      isLocked={isLocked}
      type="feed"
      as="default"
    />
  );
}
